import { FC, useEffect, useState, useCallback } from "react"
import { View, ViewStyle, Platform } from "react-native"
import { observer } from "mobx-react-lite"
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  Easing,
} from "react-native-reanimated"

import { AnimatedCountdownTimer } from "@/components/AnimatedCountdownTimer"
import { B2cRescheduleModal } from "@/components/B2cRescheduleModal"
import { Footer } from "@/components/Footer"
import { HomeButton } from "@/components/HomeButton"
import { InspectionDateCard } from "@/components/InspectionDateCard"
import { OrderBottomSheet } from "@/components/OrderBottomSheet"
import { ReferralSurvey } from "@/components/ReferralSurvey"
import { RescheduleModal } from "@/components/RescheduleModal"
import { TrackingMapView } from "@/components/TrackingMapView"
import { useStores } from "@/models"
import { colors } from "@/theme"

import { AppStackScreenProps } from "../navigators/AppNavigator"

// PLA-263: simulated drive harness (activated via ?simulate=1) for dev builds
// and staging builds exported with EXPO_PUBLIC_ENABLE_SIM=1. Both operands are
// inlined at build time, so when neither applies the branch is statically dead
// and metro/terser keep the module fully out of the bundle — same pattern as
// the ReactotronConfig require in app/app.tsx. Production must never set
// EXPO_PUBLIC_ENABLE_SIM.
let SimulatedDriveOverlay: FC | null = null
if (__DEV__ || process.env.EXPO_PUBLIC_ENABLE_SIM === "1") {
  SimulatedDriveOverlay = require("@/devtools/SimulatedDriveOverlay").SimulatedDriveOverlay
}

// PLA-287: complete HBS lifecycle sim harness (activated via ?simulate=hbs).
// Same build-time gating as the PLA-263 movement harness above, so metro strips
// it from production; distinct activation param so the two coexist.
let HbsSimOverlay: FC | null = null
if (__DEV__ || process.env.EXPO_PUBLIC_ENABLE_SIM === "1") {
  HbsSimOverlay = require("@/devtools/HbsSimOverlay").HbsSimOverlay
}

// Removed static height calculation - will be dynamic

interface OrderTrackingScreenProps extends AppStackScreenProps<"OrderTracking"> {}

export const OrderTrackingScreen: FC<OrderTrackingScreenProps> = observer(
  function OrderTrackingScreen(_props) {
    const { orderTrackingStore } = useStores()
    const { route } = _props

    // Show countdown only when URL includes "success" and no order is loaded yet
    const [showCountdown, setShowCountdown] = useState(false)
    const [surveyShowing, setSurveyShowing] = useState(false)

    // Animate the countdown overlay upward when survey appears (survey is now separate)
    const overlayTranslateY = useSharedValue(0)
    useEffect(() => {
      overlayTranslateY.value = withTiming(surveyShowing ? -80 : 0, {
        duration: 400,
        easing: Easing.out(Easing.cubic),
      })
    }, [surveyShowing])
    const overlayAnimatedStyle = useAnimatedStyle(() => ({
      transform: [{ translateY: overlayTranslateY.value }],
    }))

    // Check URL for "success" path on mount
    useEffect(() => {
      if (Platform.OS === "web" && typeof window !== "undefined") {
        const isSuccessPage = window.location.pathname.includes("/success/")
        setShowCountdown(isSuccessPage)

        // Set flag in store BEFORE order loads (for delayed resync)
        orderTrackingStore.setCameFromSuccessPage(isSuccessPage)
      }
    }, [orderTrackingStore])

    // Hide countdown when order loads (but keep survey alive if active)
    useEffect(() => {
      if (orderTrackingStore.currentOrder) {
        setShowCountdown(false)
      }
    }, [orderTrackingStore.currentOrder])

    // Rendering with driver location data

    // Parse route params - supports both formats:
    // 1. Token-only: /track/{token} -> params: { token: string }
    // 2. Full format: /track/{orderId}/{trackingToken} -> params: { orderId: string, trackingToken: string }
    const params = route.params || {}

    // Check if params might be passed in different ways
    let isTokenOnly = "token" in params
    let orderId: string | undefined
    let trackingToken: string | undefined

    if (isTokenOnly) {
      // Token-only mode
      orderId = undefined
      // Handle double-nested token structure
      const tokenParam = (params as any).token
      if (typeof tokenParam === "object" && tokenParam?.token) {
        // Handle {"token": {"token": "value"}} structure
        trackingToken = tokenParam.token
      } else if (typeof tokenParam === "string") {
        // Handle {"token": "value"} structure
        trackingToken = tokenParam
      } else {
        trackingToken = undefined
      }
    } else if (params && typeof params === "string") {
      // Maybe params is the token string itself?
      isTokenOnly = true
      orderId = undefined
      trackingToken = params
    } else {
      // Full format with order ID and optional token
      orderId = (params as any).orderId
      trackingToken = (params as any).trackingToken || undefined
    }

    // Bottom sheet height for map bounds calculation
    const [bottomSheetHeight, setBottomSheetHeight] = useState<number | undefined>(undefined)

    // Set document title for web
    useEffect(() => {
      if (Platform.OS === "web" && typeof document !== "undefined") {
        // Set immediately
        document.title = "HolDeinePlakette Tracking"

        // Also set with a small delay to override any navigation changes
        const timeout = setTimeout(() => {
          document.title = "HolDeinePlakette Tracking"
        }, 100)

        return () => clearTimeout(timeout)
      }
      return undefined
    }, [])

    // Handle URL replacement from /success to /track after map loads with 3s delay
    const handleMapLoaded = useCallback(() => {
      if (Platform.OS === "web" && typeof window !== "undefined") {
        if (window.location.pathname.includes("/success/")) {
          setTimeout(() => {
            const newPath = window.location.pathname.replace("/success/", "/track/")

            // Change URL without reload or navigation - GTM already fired on /success
            window.history.replaceState(null, "", newPath)
          }, 3000) // 3 second delay
        }
      }
    }, [])

    useEffect(() => {
      // Load order when screen mounts
      if (isTokenOnly && trackingToken) {
        // Start by showing all locations and loading them
        orderTrackingStore.setShowAllLocations(true)
        orderTrackingStore.loadAllLocations()

        // Token-only mode: backend will look up the order ID
        // Loading order by tracking token
        orderTrackingStore.loadOrderByToken(trackingToken).catch((error) => {
          // Order not found (404) - start retry loop with Pusher
          if (error.message?.includes("not found") || error.message?.includes("404")) {
            // Start sync retrying to poll with exponential backoff and listen for Pusher
            orderTrackingStore.startSyncRetrying()
          }
        })
      } else if (orderId) {
        // Start by showing all locations and loading them
        orderTrackingStore.setShowAllLocations(true)
        orderTrackingStore.loadAllLocations()

        // Full format with order ID and optional token
        orderTrackingStore.loadOrder(orderId, trackingToken || undefined)
      }

      // No demo mode - always require real data

      return () => {
        // Cleanup when unmounting
        orderTrackingStore.resetState()
      }
    }, [orderId, trackingToken, isTokenOnly, orderTrackingStore])

    // Handle transition from all locations to order view when data loads
    useEffect(() => {
      if (orderTrackingStore.currentOrder && orderTrackingStore.showAllLocations) {
        // Order data has loaded - transition to order view after brief display
        setTimeout(() => {
          orderTrackingStore.transitionToOrderView()
        }, 2000) // Show all locations for 2 seconds then transition to order view
      }
    }, [orderTrackingStore, orderTrackingStore.currentOrder, orderTrackingStore.showAllLocations])

    return (
      <View style={$root}>
        {/* Map takes exactly 100% of visible viewport — dvh respects iOS Safari URL bar */}
        <View style={[$mapContainer, { height: "100dvh" as any }]}>
          <TrackingMapView
            driverLocation={orderTrackingStore.currentOrder?.currentDriverLocation || null}
            customerLocation={orderTrackingStore.customerLocation}
            pickupLocation={orderTrackingStore.pickupLocation}
            customerAddress={orderTrackingStore.currentOrder?.customerLocation?.address}
            showRoute={true}
            routeToCustomer={orderTrackingStore.routeToCustomer}
            orderPhase={orderTrackingStore.currentOrder?.numericStatus}
            isDriverReturning={orderTrackingStore.currentOrder?.isDriverReturning || false}
            isDriveby={orderTrackingStore.currentOrder?.isDriveby || false}
            onMapLoaded={handleMapLoaded}
            allLocations={orderTrackingStore.allLocations.slice()}
            showAllLocations={orderTrackingStore.showAllLocations}
            onTransitionToOrder={() => orderTrackingStore.transitionToOrderView()}
            bottomSheetHeight={bottomSheetHeight}
          />

          {/* Home Button - Top Left */}
          <HomeButton style={$homeButton} />

          {/* Inspection Date Card - Top Right */}
          <InspectionDateCard
            date={orderTrackingStore.currentOrder?.pickupTime || null}
            style={$inspectionDateCard}
          />

          {/* Animated Countdown Timer Overlay */}
          {showCountdown && (
            <Animated.View
              pointerEvents="box-none"
              style={[$countdownOverlay, overlayAnimatedStyle]}
            >
              <View pointerEvents="none">
                <AnimatedCountdownTimer
                  duration={39}
                  showDetailedMessage={true}
                  compact={surveyShowing}
                  onComplete={() => {
                    // Keep showing the "Entschuldige..." message with details
                  }}
                />
              </View>
            </Animated.View>
          )}
        </View>

        {/* Survey — stays alive even after order loads, until user completes or dismisses */}
        {(showCountdown || surveyShowing) && (
          <View style={$surveyContainer}>
            <ReferralSurvey
              trackingToken={trackingToken || ""}
              visible={showCountdown}
              onSurveyShow={() => setSurveyShowing(true)}
              onSurveyHide={() => setSurveyShowing(false)}
            />
          </View>
        )}

        {/* Bottom sheet - Web compatible version */}
        <OrderBottomSheet onHeightChange={setBottomSheetHeight} surveyActive={surveyShowing} />

        {/* Footer */}
        <Footer style={$footer} />

        {/* Dev-only simulated drive controls (PLA-263) — null in production */}
        {SimulatedDriveOverlay ? <SimulatedDriveOverlay /> : null}

        {/* Dev-only complete HBS lifecycle sim controls (PLA-287, ?simulate=hbs) — null in production */}
        {HbsSimOverlay ? <HbsSimOverlay /> : null}

        {/* Reschedule Modal for no-show status — HDP booking widget */}
        <RescheduleModal
          visible={orderTrackingStore.showRescheduleModal}
          onClose={() => orderTrackingStore.hideRescheduleModal()}
          hubName={orderTrackingStore.currentOrder?.hubLocation?.name}
        />

        {/* B2C Reschedule Modal (for b2c-website orders) */}
        <B2cRescheduleModal
          visible={orderTrackingStore.showB2cRescheduleModal}
          onClose={() => orderTrackingStore.hideB2cRescheduleModal()}
          onSubmit={(data) => orderTrackingStore.rescheduleB2cOrder(data)}
          isLoading={orderTrackingStore.isReschedulingB2c}
          errorMessage={orderTrackingStore.rescheduleErrorB2c}
          errorCode={orderTrackingStore.rescheduleErrorCodeB2c}
          onClearError={() => orderTrackingStore.clearRescheduleErrorB2c()}
          succeeded={orderTrackingStore.rescheduleSucceededB2c}
          confirmedSlot={orderTrackingStore.confirmedSlot}
          intent={orderTrackingStore.b2cPickerIntent}
          onConfirmCancel={() => orderTrackingStore.cancelB2cOrder()}
          isCancelling={orderTrackingStore.isCancellingB2c}
          cancelErrorMessage={orderTrackingStore.cancelErrorB2c}
          locationName={orderTrackingStore.currentOrder?.hubLocation?.name}
          trackingToken={orderTrackingStore.trackingToken || ""}
          initialDate={orderTrackingStore.currentOrder?.scheduledPickupTime}
          initialTime={undefined}
          serviceType={orderTrackingStore.currentOrder?.serviceType}
        />
      </View>
    )
  },
)

const $root: ViewStyle = {
  flex: 1,
  backgroundColor: colors.surface,
  position: "relative",
}

const $mapContainer: ViewStyle = {
  width: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
}

const $footer: ViewStyle = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 2000, // Higher than bottom sheet's z-index (1000)
}

const $countdownOverlay: ViewStyle = {
  position: "absolute",
  top: "10%", // Positioned higher to allow space for survey card below timer
  left: 0,
  right: 0,
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1500, // Above map but below other controls
  paddingHorizontal: 20, // Add padding to prevent message card from being cut off
}

const $surveyContainer: ViewStyle = {
  position: "absolute",
  bottom: "32%", // Above the bottom sheet (~30% collapsed height)
  left: 0,
  right: 0,
  alignItems: "center",
  zIndex: 1500, // Above bottom sheet (1000) and map
  paddingHorizontal: 20,
}

const $homeButton: ViewStyle = {
  position: "absolute",
  top: 20,
  left: 20,
  zIndex: 1000, // Above map but below bottom sheet
}

const $inspectionDateCard: ViewStyle = {
  position: "absolute",
  top: 20,
  right: 20,
  zIndex: 1000, // Above map but below bottom sheet
}
